import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MarketingNav } from '@/components/landing/MarketingNav';
import { LandingFooter } from '@/components/landing/LandingFooter';
import { SEOHead } from '@/components/SEOHead';
import { SchemaMarkup, organizationSchema, generateBreadcrumbSchema } from '@/components/SchemaMarkup';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { Badge } from '@/components/ui/badge';
import { Calendar, Clock } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface BlogPost {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  category: string | null;
  published_at: string;
  reading_time: number | null;
  cover_image: string | null;
  language: string;
  is_published: boolean;
}

export default function BlogListPage() {
  const { t } = useTranslation();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);

  const { i18n } = useTranslation();
  const blogLang = i18n.language?.startsWith('pt') ? 'pt' : 'en';

  useEffect(() => {
    supabase
      .from('blog_posts')
      .select('id, slug, title, excerpt, category, published_at, reading_time, cover_image, language, is_published')
      .eq('is_published', true)
      .eq('language', blogLang)
      .order('published_at', { ascending: false })
      .then(({ data }) => {
        setPosts((data as BlogPost[]) || []);
        setLoading(false);
      });
  }, [blogLang]);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title={blogLang === 'pt' ? "Blog RevOps AI — RevOps, Forecast e Receita" : "RevOps AI Blog — RevOps, Forecast & Revenue"}
        description={blogLang === 'pt' ? "Artigos, guias e insights sobre RevOps, Revenue Intelligence, previsibilidade de receita e gestão de pipeline." : "Articles, guides and insights about RevOps, Revenue Intelligence, revenue predictability and pipeline management."}
        path="/blog"
      />
      <SchemaMarkup schema={[organizationSchema, generateBreadcrumbSchema([
        { name: 'Home', url: 'https://revops.digitais.ai/' },
        { name: 'Blog', url: 'https://revops.digitais.ai/blog' },
      ])]} />
      <MarketingNav />
      <main id="main-content" className="py-16">
        <div className="container">
          <Breadcrumbs items={[
            { label: 'Home', href: '/' },
            { label: 'Blog' },
          ]} />
          <h1 className="text-4xl font-bold text-[#1A1F3C] mb-4">
            Blog RevOps AI
          </h1>
          <p className="text-lg text-[#1A1F3C]/60 mb-4 max-w-2xl">
            {blogLang === 'pt'
              ? 'Artigos, guias e insights sobre RevOps, Revenue Intelligence, previsibilidade de receita e gestão de pipeline.'
              : 'Articles, guides and insights about RevOps, Revenue Intelligence, revenue predictability and pipeline management.'}
          </p>
          <p className="text-sm text-[#1A1F3C]/40 mb-12 max-w-3xl">
            {blogLang === 'pt'
              ? 'Explore conteúdos práticos sobre como estruturar sua operação de receita, melhorar taxas de conversão, implementar forecast matemático e usar dados do CRM para tomar decisões mais inteligentes. Publicamos semanalmente artigos escritos por especialistas em Revenue Operations.'
              : 'Explore practical content on how to structure your revenue operation, improve conversion rates, implement mathematical forecasting and use CRM data to make smarter decisions. We publish weekly articles written by Revenue Operations experts.'}
          </p>

          {loading ? (
            <div className="text-center py-12 text-muted-foreground">Carregando...</div>
          ) : (
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {posts.map(post => (
                <Link
                  key={post.id}
                  to={`/blog/${post.slug}`}
                  className="group block rounded-xl border border-[#1A1F3C]/10 overflow-hidden hover:shadow-lg transition-shadow"
                >
                  {post.cover_image && (
                    <div className="aspect-video bg-muted overflow-hidden">
                      <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" />
                    </div>
                  )}
                  <div className="p-5">
                    {post.category && (
                      <Badge variant="secondary" className="mb-3 text-xs">{post.category}</Badge>
                    )}
                    <h2 className="text-lg font-semibold text-[#1A1F3C] group-hover:text-[#4F6CFF] transition-colors mb-2 line-clamp-2">
                      {post.title}
                    </h2>
                    {post.excerpt && (
                      <p className="text-sm text-[#1A1F3C]/60 mb-4 line-clamp-3">{post.excerpt}</p>
                    )}
                    <div className="flex items-center gap-4 text-xs text-[#1A1F3C]/40">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {formatDate(post.published_at)}
                      </span>
                      {post.reading_time && (
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {post.reading_time} min
                        </span>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>
      <LandingFooter />
    </div>
  );
}
